/**
 * The row of actions under an answer: copy it, or print it.
 *
 * Quiet until hovered — an answer you are reading should not be framed by
 * buttons — but always reachable by keyboard.
 */

import { useCallback, useState } from "react";
import { Check, Copy, Printer } from "lucide-react";
import { copyText } from "../markdown/copy.ts";
import { Button } from "./ui/button.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip.tsx";

export function AnswerActions({ content }: { content: string }) {
  const [copied, setCopied] = useState(false);

  const copy = useCallback(async () => {
    // The stored markdown, not the rendering: `$\nabla$` is what pastes usefully
    // into a notes app, and the on-screen glyph is not.
    if (await copyText(content)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    }
  }, [content]);

  return (
    <div className="no-print mt-4 flex items-center gap-1 opacity-0 transition-opacity focus-within:opacity-100 group-hover/answer:opacity-100">
      <Action
        label={copied ? "Copied" : "Copy as Markdown"}
        onClick={() => void copy()}
      >
        {copied ? <Check className="text-success" /> : <Copy />}
      </Action>

      <Action label="Print this answer" onClick={() => window.print()}>
        <Printer />
      </Action>
    </div>
  );
}

function Action({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onClick}
          aria-label={label}
          className="text-muted-foreground hover:text-foreground"
        >
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}
